/** Pure parsers for the lineup import (CSV and M3U); no React, so lab scripts can use them too. */

/** CSV: number,name,category,host,port[,protocol] — header row optional. */
export function parseLineupCsv(text: string) {
  const rows: { number: number; name: string; category?: string; host?: string; port?: number; protocol?: string; url?: string }[] = [];
  for (const line of text.split(/\r?\n/)) {
    const cells = line.split(/[,;\t]/).map((c) => c.trim());
    if (cells.length < 2 || !/^\d+$/.test(cells[0]!)) continue; // skips header and blank lines
    const [number, name, category, hostOrUrl, port, protocol] = cells;
    const isUrl = /^[a-z]+:\/\//i.test(hostOrUrl ?? '');
    rows.push({
      number: Number(number),
      name: name!,
      category: category || undefined,
      ...(isUrl ? { url: hostOrUrl, protocol: protocol || 'HLS' } : { host: hostOrUrl || undefined, port: port ? Number(port) : undefined }),
      ...(protocol && !isUrl ? { protocol } : {}),
    });
  }
  return rows;
}

export interface M3uEntry {
  name: string;
  group: string;
  logo?: string;
  url: string;
}

/** Players that need a web page (YouTube, Twitch…) are not streams a TV can play. */
const NOT_A_STREAM = /youtube\.com|youtu\.be|twitch\.tv|dailymotion\.com|facebook\.com/i;

/** #EXTM3U playlist → entries (title / tvg-logo / group-title); marks like Ⓖ Ⓢ (Free-TV) are dropped from names. */
export function parseM3u(text: string): M3uEntry[] {
  const out: M3uEntry[] = [];
  let info: { name: string; group: string; logo?: string } | null = null;
  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim();
    if (line.startsWith('#EXTINF')) {
      const attr = (k: string) => new RegExp(`${k}="([^"]*)"`, 'i').exec(line)?.[1]?.trim();
      // The title follows the last comma that is outside the attribute quotes.
      const lastQuote = line.lastIndexOf('"');
      const comma = line.indexOf(',', lastQuote >= 0 ? lastQuote : 0);
      const title = comma >= 0 ? line.slice(comma + 1).trim() : '';
      const name = (title || attr('tvg-name') || '').replace(/[Ⓐ-ⓩ]/g, '').replace(/\s+/g, ' ').trim();
      info = { name, group: attr('group-title') || 'Imported', logo: attr('tvg-logo') || undefined };
    } else if (line && !line.startsWith('#') && info) {
      if (/^[a-z]+:\/\//i.test(line) && !NOT_A_STREAM.test(line) && info.name) out.push({ ...info, url: line });
      info = null;
    }
  }
  return out;
}

export function m3uRow(e: M3uEntry, number: number) {
  const base = { number, name: e.name.slice(0, 120), category: e.group, logoUrl: e.logo };
  const mc = /^(udp|rtp):\/\/@?([\d.]+):(\d+)/i.exec(e.url);
  if (mc) return { ...base, protocol: mc[1]!.toLowerCase() === 'udp' ? 'UDP_MULTICAST' : 'RTP_MULTICAST', host: mc[2], port: Number(mc[3]) };
  const path = e.url.split('?')[0]!.toLowerCase();
  return { ...base, url: e.url, protocol: path.endsWith('.mpd') ? 'DASH' : path.endsWith('.ts') ? 'HTTP_TS' : 'HLS' };
}

